import { SetStateAction, useCallback, useState } from "react";
import moment from "moment";
import type { IFactura } from "../Facturas";
import { EstadoFacturacion } from "./EstadoFacturacionIcon";

// Fechas de los listados de facturas (Facturas y FacturasParciales): los dos filtran y muestran la fecha de expedición
// (la del PDF: la de emisión si está emitida, la de creación si no). La fecha de pago es solo una columna.

// Fecha de expedición de una fila; las filas creadas en pantalla sin ella usan la de creación.
export const fechaExpedicionFila = (r: IFactura): string | null => r.fechaExpedicion ?? r.fechaFactura;

export const fechaListado = (fecha?: string | null): string => fecha ? new Date(fecha).toLocaleDateString("es-ES") : "";

// Filtro "Desde / Hasta" del listado, por fecha de expedición (días completos).
export const dentroDelPeriodo = (r: IFactura, desde: Date | null, hasta: Date | null): boolean => {
  const fecha = fechaExpedicionFila(r);
  const m = fecha ? moment(fecha) : null;
  if (desde && (!m || m.isBefore(moment(desde).startOf("day")))) return false;
  if (hasta && (!m || m.isAfter(moment(hasta).endOf("day")))) return false;
  return true;
};

// Al emitir un Borrador desde un diálogo, el listado casi siempre recibe solo el estado nuevo (no la fecha de emisión).
// La emisión es "ahora" (el servidor guarda la fecha de emisión en ese momento), así que su fecha de expedición pasa a
// hoy y la fila se mueve en el filtro de fechas sin recargar. Si el diálogo ya trae la fecha (la fila llega con otra
// fechaExpedicion, p. ej. al detectar que otra pestaña la emitió antes), se respeta la suya.
const ESTADOS_BORRADOR: string[] = [EstadoFacturacion.PENDIENTE_PAGO, EstadoFacturacion.PAGADA];
const ESTADOS_EMITIDA: string[] = [EstadoFacturacion.EMITIDA, EstadoFacturacion.EMITIDA_PENDIENTE_PAGO];

export const sincronizarFechaExpedicion = (antes: IFactura[], despues: IFactura[]): IFactura[] => {
  const previas = new Map(antes.map(r => [r._id, r]));
  return despues.map(r => {
    const previa = previas.get(r._id);
    const recienEmitida = !!previa && ESTADOS_BORRADOR.includes(previa.estado) && ESTADOS_EMITIDA.includes(r.estado)
      && r.fechaExpedicion === previa.fechaExpedicion;
    return recienEmitida ? { ...r, fechaExpedicion: new Date().toISOString() } : r;
  });
};

// Estado de las filas de un listado: toda actualización pasa por sincronizarFechaExpedicion.
export const useFilasListado = (): [IFactura[], (accion: SetStateAction<IFactura[]>) => void] => {
  const [filas, setFilasBase] = useState<IFactura[]>([]);
  const setFilas = useCallback((accion: SetStateAction<IFactura[]>) =>
    setFilasBase(prev => sincronizarFechaExpedicion(prev, typeof accion === "function" ? accion(prev) : accion)), []);
  return [filas, setFilas];
};
